/**
 * SubgroepForm - Formulier voor het aanmaken van nieuwe subgroepen
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiFetch from '../../services/api';
import ErrorDialog from '../../components/ErrorDialog';
import Toast from '../../components/Toast';
import './Voorraad.css';

interface VoorraadCategorie {
  id: number;
  code: string;
  naam: string;
}

interface VoorraadGroep {
  id: number;
  parent_id: number | null;
  categorie_id: number;
  naam: string;
  niveau: number;
}

const SubgroepForm = () => {
  const [naam, setNaam] = useState('');
  const [categorieId, setCategorieId] = useState<number | null>(null);
  const [parentId, setParentId] = useState<number | null>(null);
  const [categorieen, setCategorieen] = useState<VoorraadCategorie[]>([]);
  const [groepen, setGroepen] = useState<VoorraadGroep[]>([]);
  const [alleGroepen, setAlleGroepen] = useState<VoorraadGroep[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteGroepId, setDeleteGroepId] = useState<number | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Categorie verwijderen state
  const [deleteCategorieId, setDeleteCategorieId] = useState<number | null>(null);
  const [deletingCategorie, setDeletingCategorie] = useState(false);
  const [showDeleteCategorieModal, setShowDeleteCategorieModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const navigate = useNavigate();

  // Nieuwe hoofdcategorie state
  const [nieuweCategorieNaam, setNieuweCategorieNaam] = useState('');
  const [savingCategorie, setSavingCategorie] = useState(false);

  // Hernoemen state
  const [renameType, setRenameType] = useState<'categorie' | 'groep'>('categorie');
  const [renameCategorieId, setRenameCategorieId] = useState<number | null>(null);
  const [renameGroepId, setRenameGroepId] = useState<number | null>(null);
  const [renameNaam, setRenameNaam] = useState('');
  const [savingRename, setSavingRename] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Laad groepen wanneer categorie verandert
    if (categorieId) {
      loadGroepen(categorieId);
    } else {
      setGroepen([]);
      setParentId(null);
    }
  }, [categorieId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const categorieenData = await apiFetch('/api/v1/voorraad/categorieen');
      setCategorieen(categorieenData);

      // Laad alle groepen van alle categorieën (inclusief subgroepen)
      const allGroups: VoorraadGroep[] = [];
      for (const cat of categorieenData) {
        const groepenData = await apiFetch(`/api/v1/voorraad/groepen?categorie_id=${cat.id}&alle=true`);
        allGroups.push(...groepenData);
      }
      setAlleGroepen(allGroups);
    } catch (err: any) {
      setError(err.message || 'Fout bij laden van categorieën');
    } finally {
      setLoading(false);
    }
  };

  const loadGroepen = async (catId: number) => {
    try {
      // Laad alle groepen inclusief subgroepen
      const groepenData = await apiFetch(`/api/v1/voorraad/groepen?categorie_id=${catId}&alle=true`);
      setGroepen(groepenData);
    } catch (err: any) {
      console.error('Fout bij laden van groepen:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!naam.trim()) {
      setError('Vul een naam in voor de subgroep');
      return;
    }

    if (!categorieId) {
      setError('Selecteer een categorie');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      await apiFetch('/api/v1/voorraad/groepen', {
        method: 'POST',
        body: JSON.stringify({
          naam: naam.trim(),
          categorie_id: categorieId,
          parent_id: parentId,
          actief: true
        })
      });

      // Reset form en herlaad data
      setNaam('');
      setCategorieId(null);
      setParentId(null);
      setSuccess('Subgroep succesvol aangemaakt!');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan van subgroep');
    } finally {
      setSaving(false);
    }
  };

  const handleCategorieSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nieuweCategorieNaam.trim()) {
      setError('Vul een naam in voor de hoofdcategorie');
      return;
    }

    try {
      setSavingCategorie(true);
      setError(null);

      await apiFetch('/api/v1/voorraad/categorieen', {
        method: 'POST',
        body: JSON.stringify({
          naam: nieuweCategorieNaam.trim()
        })
      });

      setNieuweCategorieNaam('');
      setSuccess('Hoofdcategorie succesvol aangemaakt!');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan van hoofdcategorie');
    } finally {
      setSavingCategorie(false);
    }
  };

  const handleRenameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!renameNaam.trim()) {
      setError('Vul een nieuwe naam in');
      return;
    }

    if (renameType === 'categorie' && !renameCategorieId) {
      setError('Selecteer een hoofdcategorie om te hernoemen');
      return;
    }

    if (renameType === 'groep' && !renameGroepId) {
      setError('Selecteer een subgroep om te hernoemen');
      return;
    }

    try {
      setSavingRename(true);
      setError(null);

      if (renameType === 'categorie') {
        await apiFetch(`/api/v1/voorraad/categorieen/${renameCategorieId}`, {
          method: 'PUT',
          body: JSON.stringify({ naam: renameNaam.trim() })
        });
      } else {
        await apiFetch(`/api/v1/voorraad/groepen/${renameGroepId}`, {
          method: 'PUT',
          body: JSON.stringify({ naam: renameNaam.trim() })
        });
      }

      setRenameNaam('');
      setRenameCategorieId(null);
      setRenameGroepId(null);
      setSuccess(`${renameType === 'categorie' ? 'Hoofdcategorie' : 'Subgroep'} succesvol hernoemd!`);
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij hernoemen');
    } finally {
      setSavingRename(false);
    }
  };

  const handleDeleteClick = () => {
    if (!deleteGroepId) {
      setError('Selecteer een subgroep om te verwijderen');
      return;
    }
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteModal(false);

    try {
      setDeleting(true);
      setError(null);

      await apiFetch(`/api/v1/voorraad/groepen/${deleteGroepId}`, {
        method: 'DELETE'
      });

      setDeleteGroepId(null);
      setSuccess('Subgroep succesvol verwijderd!');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen van subgroep');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCategorieClick = () => {
    if (!deleteCategorieId) {
      setError('Selecteer een hoofdcategorie om te verwijderen');
      return;
    }
    setShowDeleteCategorieModal(true);
  };

  const handleDeleteCategorieConfirm = async () => {
    setShowDeleteCategorieModal(false);

    try {
      setDeletingCategorie(true);
      setError(null);

      await apiFetch(`/api/v1/voorraad/categorieen/${deleteCategorieId}`, {
        method: 'DELETE'
      });

      setDeleteCategorieId(null);
      setSuccess('Hoofdcategorie succesvol verwijderd!');
      loadData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen van hoofdcategorie');
    } finally {
      setDeletingCategorie(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Voorraad Groepen Bewerken</h1>
        </div>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          Laden...
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Voorraad Groepen Bewerken</h1>
      </div>

      <Toast message={success} type="success" onClose={() => setSuccess(null)} />

      {/* Nieuwe Hoofdcategorie sectie */}
      <form onSubmit={handleCategorieSubmit} style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#1e40af' }}>Nieuwe Hoofdcategorie</h2>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
          Hoofdcategorieën verschijnen in het Voorraad dropdown menu in de header.
        </p>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 500,
              color: '#374151'
            }}>
              Naam *
            </label>
            <input
              type="text"
              value={nieuweCategorieNaam}
              onChange={(e) => setNieuweCategorieNaam(e.target.value)}
              placeholder="Bijv. Gereedschap, Veiligheid, etc."
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px'
              }}
            />
          </div>
          <button
            type="submit"
            className="btn-lg btn-primary"
            disabled={savingCategorie}
            style={{ whiteSpace: 'nowrap' }}
          >
            {savingCategorie ? 'Opslaan...' : 'Categorie Aanmaken'}
          </button>
        </div>
      </form>

      {/* Nieuwe Subgroep sectie */}
      <form onSubmit={handleSubmit} style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#059669' }}>Nieuwe Subgroep</h2>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
          Subgroepen worden aangemaakt binnen een bestaande hoofdcategorie.
        </p>
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            Categorie *
          </label>
          <select
            value={categorieId || ''}
            onChange={(e) => setCategorieId(e.target.value ? parseInt(e.target.value) : null)}
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white'
            }}
            required
          >
            <option value="">Selecteer een categorie</option>
            {categorieen.map(cat => (
              <option key={cat.id} value={cat.id}>
                {cat.naam}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            Bovenliggende groep (optioneel)
          </label>
          <select
            value={parentId || ''}
            onChange={(e) => setParentId(e.target.value ? parseInt(e.target.value) : null)}
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white'
            }}
            disabled={!categorieId}
          >
            <option value="">Geen (hoofdgroep)</option>
            {(() => {
              // Bouw hiërarchische lijst
              const renderGroepen = (parentIdFilter: number | null, niveau: number): JSX.Element[] => {
                return groepen
                  .filter(g => g.parent_id === parentIdFilter)
                  .map(groep => {
                    const prefix = '─ '.repeat(niveau);
                    const children = renderGroepen(groep.id, niveau + 1);
                    return [
                      <option key={groep.id} value={groep.id}>
                        {prefix}{groep.naam}
                      </option>,
                      ...children
                    ];
                  })
                  .flat();
              };
              return renderGroepen(null, 0);
            })()}
          </select>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '0.25rem' }}>
            Laat leeg om een hoofdgroep te maken, of selecteer een bestaande groep om een subgroep te maken.
          </p>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            Naam *
          </label>
          <input
            type="text"
            value={naam}
            onChange={(e) => setNaam(e.target.value)}
            placeholder="Bijv. Ankers, Kompassen, etc."
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px'
            }}
            required
          />
        </div>

        <div className="form-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="btn-lg btn-secondary"
            onClick={() => navigate('/voorraad')}
          >
            Annuleren
          </button>
          <button
            type="submit"
            className="btn-lg btn-primary"
            disabled={saving}
          >
            {saving ? 'Opslaan...' : 'Subgroep Aanmaken'}
          </button>
        </div>
      </form>

      {/* Hernoemen sectie */}
      <form onSubmit={handleRenameSubmit} style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#d97706' }}>Hernoemen</h2>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
          Wijzig de naam van een hoofdcategorie of subgroep.
        </p>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            Wat wil je hernoemen?
          </label>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="renameType"
                checked={renameType === 'categorie'}
                onChange={() => {
                  setRenameType('categorie');
                  setRenameGroepId(null);
                  setRenameNaam('');
                }}
              />
              Hoofdcategorie
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="radio"
                name="renameType"
                checked={renameType === 'groep'}
                onChange={() => {
                  setRenameType('groep');
                  setRenameCategorieId(null);
                  setRenameNaam('');
                }}
              />
              Subgroep
            </label>
          </div>
        </div>

        {renameType === 'categorie' ? (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 500,
              color: '#374151'
            }}>
              Selecteer hoofdcategorie
            </label>
            <select
              value={renameCategorieId || ''}
              onChange={(e) => {
                const id = e.target.value ? parseInt(e.target.value) : null;
                setRenameCategorieId(id);
                if (id) {
                  const cat = categorieen.find(c => c.id === id);
                  setRenameNaam(cat?.naam || '');
                } else {
                  setRenameNaam('');
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Selecteer een hoofdcategorie</option>
              {categorieen.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.naam}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 500,
              color: '#374151'
            }}>
              Selecteer subgroep
            </label>
            <select
              value={renameGroepId || ''}
              onChange={(e) => {
                const id = e.target.value ? parseInt(e.target.value) : null;
                setRenameGroepId(id);
                if (id) {
                  const groep = alleGroepen.find(g => g.id === id);
                  setRenameNaam(groep?.naam || '');
                } else {
                  setRenameNaam('');
                }
              }}
              style={{
                width: '100%',
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Selecteer een subgroep</option>
              {alleGroepen.map(groep => {
                const cat = categorieen.find(c => c.id === groep.categorie_id);
                return (
                  <option key={groep.id} value={groep.id}>
                    {cat?.naam} → {'  '.repeat(groep.niveau)}{groep.naam}
                  </option>
                );
              })}
            </select>
          </div>
        )}

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            Nieuwe naam *
          </label>
          <input
            type="text"
            value={renameNaam}
            onChange={(e) => setRenameNaam(e.target.value)}
            placeholder="Vul de nieuwe naam in"
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '1px solid #d1d5db',
              borderRadius: '6px'
            }}
          />
        </div>

        <div className="form-actions" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            type="submit"
            className="btn-lg"
            disabled={savingRename}
            style={{
              backgroundColor: savingRename ? '#fcd34d' : '#d97706',
              color: 'white',
              padding: '0.75rem 1.5rem',
              border: 'none',
              borderRadius: '6px',
              cursor: savingRename ? 'not-allowed' : 'pointer',
              fontWeight: 500
            }}
          >
            {savingRename ? 'Opslaan...' : 'Naam Wijzigen'}
          </button>
        </div>
      </form>

      {/* Verwijder sectie */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        <h2 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc2626' }}>Verwijderen</h2>
        <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
          Verwijder een hoofdcategorie (alleen als er geen groepen in zitten) of een subgroep (alleen als er geen items in zitten).
        </p>

        {/* Hoofdcategorie verwijderen */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            Hoofdcategorie
          </label>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select
              value={deleteCategorieId || ''}
              onChange={(e) => setDeleteCategorieId(e.target.value ? parseInt(e.target.value) : null)}
              style={{
                flex: 1,
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Selecteer een hoofdcategorie</option>
              {categorieen.map(cat => (
                <option key={cat.id} value={cat.id}>
                  {cat.naam}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-lg btn-danger"
              onClick={handleDeleteCategorieClick}
              disabled={deletingCategorie || !deleteCategorieId}
              style={{
                backgroundColor: deletingCategorie || !deleteCategorieId ? '#fca5a5' : '#dc2626',
                color: 'white',
                cursor: deletingCategorie || !deleteCategorieId ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {deletingCategorie ? 'Verwijderen...' : 'Verwijderen'}
            </button>
          </div>
        </div>

        {/* Subgroep verwijderen */}
        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: 500,
            color: '#374151'
          }}>
            Subgroep
          </label>
          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <select
              value={deleteGroepId || ''}
              onChange={(e) => setDeleteGroepId(e.target.value ? parseInt(e.target.value) : null)}
              style={{
                flex: 1,
                padding: '0.75rem',
                fontSize: '1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white'
              }}
            >
              <option value="">Selecteer een subgroep</option>
              {alleGroepen.map(groep => {
                const cat = categorieen.find(c => c.id === groep.categorie_id);
                return (
                  <option key={groep.id} value={groep.id}>
                    {cat?.naam} → {'  '.repeat(groep.niveau)}{groep.naam}
                  </option>
                );
              })}
            </select>
            <button
              type="button"
              className="btn-lg btn-danger"
              onClick={handleDeleteClick}
              disabled={deleting || !deleteGroepId}
              style={{
                backgroundColor: deleting || !deleteGroepId ? '#fca5a5' : '#dc2626',
                color: 'white',
                cursor: deleting || !deleteGroepId ? 'not-allowed' : 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              {deleting ? 'Verwijderen...' : 'Verwijderen'}
            </button>
          </div>
        </div>
      </div>

      {/* Verwijder bevestigingsmodal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc3545' }}>
              Subgroep Verwijderen
            </h3>
            <p style={{ color: '#666', marginBottom: '0.5rem' }}>
              Weet je zeker dat je deze subgroep wilt verwijderen?
            </p>
            <p style={{
              background: '#f8f9fa',
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <strong>{alleGroepen.find(g => g.id === deleteGroepId)?.naam}</strong>
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteModal(false)}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Annuleren
              </button>
              <button
                onClick={handleDeleteConfirm}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verwijder hoofdcategorie bevestigingsmodal */}
      {showDeleteCategorieModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '450px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#dc3545' }}>
              Hoofdcategorie Verwijderen
            </h3>
            <p style={{ color: '#666', marginBottom: '0.5rem' }}>
              Weet je zeker dat je deze hoofdcategorie wilt verwijderen?
            </p>
            <p style={{
              background: '#f8f9fa',
              padding: '0.75rem',
              borderRadius: '4px',
              marginBottom: '1.5rem'
            }}>
              <strong>{categorieen.find(c => c.id === deleteCategorieId)?.naam}</strong>
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowDeleteCategorieModal(false)}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Annuleren
              </button>
              <button
                onClick={handleDeleteCategorieConfirm}
                style={{
                  padding: '0.6rem 1.5rem',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 500
                }}
              >
                Verwijderen
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default SubgroepForm;
